import { Injectable } from '@angular/core'
import { Warehouse, WarehousesService } from '../warehouses'
import {
  Observable,
  combineLatest,
  from,
  last,
  map,
  of,
  scan,
  concatMap,
  switchMap,
} from 'rxjs'
import { Page } from '../../models/util.model'
import { StocksService } from './stocks.service'
import { uniqBy } from 'lodash'
import { StockSearchParams, StockField } from './models/stock-search.model'
import {
  StocksListingData,
  StocksListingPage,
} from './models/stock-listing.model'
import { parseStockPageKeys } from './libs/stocks-page.lib'
import { Stock } from './models/stock.entity'

@Injectable({
  providedIn: 'root',
})
export class StocksRepository {
  constructor(
    private stocksService: StocksService,
    private warehousesService: WarehousesService,
  ) {}

  /**
   * Search goods-returns and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search goods-returns
   */
  searchStocks$(
    searchParams: StockSearchParams,
    fields: StockField[],
    listingData?: StocksListingData,
    returnAll?: boolean,
  ): Observable<StocksListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.stocksService.limit

    return needsPagination
      ? this._paginateStocks$(searchParams, fields, listingData)
      : this._searchStocks$(searchParams, fields, listingData, returnAll)
  }

  /**
   * Search goods returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<StocksListingPage> for search goods-returns
   */
  private _searchStocks$(
    searchParams: StockSearchParams,
    fields: StockField[],
    listingData?: StocksListingData,
    returnAll?: boolean,
  ): Observable<StocksListingPage> {
    return this.stocksService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, fields, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Load external page goods-returns data
   * @param page - the goods-returns page
   * @param fields - the goods-returns fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: Page<Stock>,
    fields: StockField[],
    listingData?: StocksListingData,
  ): Observable<StocksListingData> {
    const extDataKeys = parseStockPageKeys(page)
    const obs$: Partial<{
      warehouses: Observable<Warehouse[]>
    }> = {}

    if (fields.includes('warehouse._id') && extDataKeys.warehouseIds) {
      const existingWarehouses = listingData?.warehouses || []
      const hasAllWarehouses = extDataKeys.warehouseIds.every((id) =>
        existingWarehouses.some((warehouse) => warehouse._id === id),
      )

      if (!hasAllWarehouses) {
        obs$.warehouses = this.warehousesService.store$(
          extDataKeys.warehouseIds,
          existingWarehouses,
        )
      } else {
        obs$.warehouses = of(existingWarehouses)
      }
    }

    if (!Object.keys(obs$).length) {
      return of({} as StocksListingData)
    }

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: StocksListingData = {}
        if (results.warehouses) extData.warehouses = results.warehouses
        return extData
      }),
    )
  }

  /**
   * Search more than 200 goods-returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<StocksListingPage> for search goods-returns
   */
  private _paginateStocks$(
    searchParams: StockSearchParams,
    fields: StockField[],
    listingData?: StocksListingData,
  ): Observable<StocksListingPage> {
    const totalCount = searchParams.limit || this.stocksService.limit
    const pagesCount = Math.ceil(totalCount / this.stocksService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.stocksService.limit,
        offset: i * this.stocksService.limit,
      })),
    ).pipe(
      concatMap((pageParams) =>
        this._searchStocks$(pageParams, fields, listingData),
      ),
      scan<StocksListingPage, StocksListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as StocksListingPage,
      ),
      last(),
    )
  }
}
